/**
 * Session expiry warning banner with its own clock.
 *
 * The workspace page does not otherwise re-render while the 10s workspace
 * poll is running — TanStack Query v5's structural sharing keeps the query
 * data referentially/deep equal while status stays "running", so an inline
 * `Date.now()` check evaluated only on render would never surface once the
 * page goes idle. This component ticks itself so the banner still appears
 * when the expiry threshold crosses.
 */

import { useEffect, useState, type ReactElement } from 'react';
import { EXPIRY_WARNING_MS } from '../utils/time';

interface ExpiryBannerProps {
  expiresAt: string | null | undefined;
}

export function ExpiryBanner({ expiresAt }: ExpiryBannerProps): ReactElement | null {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (expiresAt == null) return;
    const intervalId = setInterval(() => { setNow(Date.now()); }, 30_000);
    return () => { clearInterval(intervalId); };
  }, [expiresAt]);

  if (expiresAt == null) return null;

  const msRemaining = new Date(expiresAt).getTime() - now;
  if (msRemaining >= EXPIRY_WARNING_MS) return null;

  return (
    <div className="workspace-page__expiry-banner" role="alert">
      Session expiring soon — use Extend to keep working.
    </div>
  );
}
